/* Utils */
import { isEmpty } from 'lodash';
import { IParseCurrentStateArguments } from './types';
import {
  adjustPrefToMatchChartSettings,
  areChartSettingsMigrated,
  checkDefaultAndCurrentSliceSettings,
  deleteLegacyFields,
  foundNonUsedChartType,
  migrateChartSettingsV2,
  migrateOldExpressionLineTypes,
  parseOldChartPrefToChartSettings,
  validateResultFields,
} from './utils';
import { IChartSettings } from '../../../components/Chart/interfaces';
import { IIncidentType } from '../../../pages/Incidents/interfaces';

export default ({
  chartPref,
  analysis,
  defaultChartSettings,
}: IParseCurrentStateArguments): IChartSettings | null => {
  try {
    const parsedState = JSON.parse(chartPref);
    if (isEmpty(parsedState) || parsedState.isEmptyPreference) {
      return null;
    }
    deleteLegacyFields(parsedState);

    // if we drop off any of old chart type, and it was selected in chartSettings
    // then we should fall back to default chart settings
    if (foundNonUsedChartType(parsedState?.chartSettings?.chartType)) {
      return null;
    }
    // for debugging
    if (window.location.href.includes('/debug')) {
      // eslint-disable-next-line no-console
      console.info('parse states==', parsedState, analysis);
      // eslint-disable-next-line no-console
      console.info('default chart setting:', defaultChartSettings);
    }
    // new implementation with CHART_SETTINGS_V2
    if (
      analysis.__typename === 'Expression' ||
      analysis.__typename === 'Funnel' ||
      analysis.__typename === 'Retention' ||
      analysis.__typename === 'Path' ||
      analysis.__typename === 'DataTable' ||
      analysis.__typename === 'Significance'
    ) {
      let currentChartSettings: IChartSettings;

      // KB-6070. Migrating old expression lines to new expressionSettings
      const expressionLineType = parsedState?.chartSettings?.expressionLineType;
      if (expressionLineType) {
        migrateOldExpressionLineTypes(parsedState, expressionLineType);
      }
      // ==================================================================
      checkDefaultAndCurrentSliceSettings(
        parsedState,
        defaultChartSettings,
        analysis,
      );
      adjustPrefToMatchChartSettings(parsedState, defaultChartSettings);
      // if we have chartSettings already, we don't need to parse it
      if (areChartSettingsMigrated(parsedState, analysis.queryType)) {
        // if migrated, it means we have chart settings and no need to involve the oldChartPref
        currentChartSettings = {
          ...(parsedState.chartSettings as IChartSettings),
        };
        migrateChartSettingsV2(
          analysis,
          currentChartSettings,
          defaultChartSettings,
        );
      } else {
        const chartSettings = parseOldChartPrefToChartSettings(
          parsedState,
          defaultChartSettings,
          analysis,
        );
        if (!chartSettings) {
          return null;
        }

        currentChartSettings = {
          ...chartSettings,
        };
      }

      // KBT-1616: Interate defaultIncidentTypes,
      // update with legacyIncidentTypes and currentChartSettings.activeIncidentTypes
      // This is the migration since we didn't save the incidents in chartSettings before.
      const legacyIncidentTypes = parsedState.activeIncidentTypes as Record<
        IIncidentType['id'],
        boolean
      >;
      const defaultActiveIncidentTypes =
        defaultChartSettings.activeIncidentTypes;
      const updatedActiveIncidentTypes = Object.fromEntries(
        Object.keys(defaultActiveIncidentTypes).map((key) => [
          key,
          legacyIncidentTypes?.[key] ??
            currentChartSettings.activeIncidentTypes?.[key] ??
            false,
        ]),
      );

      return validateResultFields(
        {
          ...currentChartSettings,
          activeIncidentTypes: updatedActiveIncidentTypes,
        },
        analysis,
        defaultChartSettings,
      );
    }

    return null;
  } catch (e) {
    console.warn(
      `Failed to parse chart preference for analysis ${analysis.id}. Displaying with default preferences`,
      e,
    );
    return null;
  }
};
